
import { DataService } from '../utilities/data.service';
import { GetConsentsService } from './get-consents.service';
import { Consent } from '../../dataTypes/type-consent';

/**
 * Service responsible for changing status of consents
 */
export class ConsentsStatusService {

    static selectAllStatus = {
        status: false,
        isIntermediateState: false,
        categories: {
            status: false,
            isIntermediateState: false
        }
    }

    /**
     * similar to consentsService.setSelectedStateForAllCategories
     * we will use unsavedConsentStatus for setting it
     * it will call setSelectAllState and setSelectedStateOfCategory(for each category)
     * @param categories 
     */
    static setSelectedStateForCategories(categories: Consent.Category[]): void {
        categories.forEach(category => {
            ConsentsStatusService.setSelectedStateOfCategory(category);
        });
        ConsentsStatusService.setSelectAllState();
    }

    /**
     * It'll check selected state of all consents in particular category
     * and if 
     *  all true then set category status=true and isIntermediateState=false
     *  all false then set category status=false and isIntermediateState=false
     *  else set category status=false and isIntermediateState=true
     * @param category 
     */
    static setSelectedStateOfCategory(category: Consent.Category): void {
        const status = ConsentsStatusService.checkOverallStatusByConsents(category.consents);
        if (status === Consent.CategoryStatus.intermediate) {
            category.status = false;
            category.isIntermediateState = true;
        } else if (status === Consent.CategoryStatus.yes) {
            category.status = true;
            category.isIntermediateState = false;
        } else {
            category.status = false;
            category.isIntermediateState = false;
        }
    }

    /**
     * It'll check selected state of all consents
     * and if 
     *  all true then set category status=true and isIntermediateState=false
     *  all false then set category status=false and isIntermediateState=false
     *  else set category status=false and isIntermediateState=true
     * @param category 
     */
    static setSelectAllState(): void {
        const status = ConsentsStatusService.checkOverallStatusByConsents(DataService.consents);
        if (status === Consent.CategoryStatus.intermediate) {
            ConsentsStatusService.selectAllStatus.status = false;
            ConsentsStatusService.selectAllStatus.isIntermediateState = true;
        } else if (status === Consent.CategoryStatus.yes) {
            ConsentsStatusService.selectAllStatus.status = true;
            ConsentsStatusService.selectAllStatus.isIntermediateState = false;
        } else {
            ConsentsStatusService.selectAllStatus.status = false;
            ConsentsStatusService.selectAllStatus.isIntermediateState = false;
        }
    }

    /**
     * It'll check selected state of all consents
     * and if 
     *  all true then set category status=true and isIntermediateState=false
     *  all false then set category status=false and isIntermediateState=false
     *  else set category status=false and isIntermediateState=true
     * @param category 
     */
    static setSelectAllCategoryState(): void {
        const status = ConsentsStatusService.checkOverallStatusByCategory(DataService.categories);
        if (status === Consent.CategoryStatus.intermediate) {
            ConsentsStatusService.selectAllStatus.categories.status = false;
            ConsentsStatusService.selectAllStatus.categories.isIntermediateState = true;
        } else if (status === Consent.CategoryStatus.yes) {
            ConsentsStatusService.selectAllStatus.categories.status = true;
            ConsentsStatusService.selectAllStatus.categories.isIntermediateState = false;
        } else {
            ConsentsStatusService.selectAllStatus.categories.status = false;
            ConsentsStatusService.selectAllStatus.categories.isIntermediateState = false;
        }
    }

    /**
     * Checks status of given consents to determine their overal status "yes", "no" or "intermediate"
     * @param consents 
     */
    static checkOverallStatusByConsents(consents: Consent.Consent[]): Consent.CategoryStatus {
        const givenConsents = consents.filter((e) => {
            return e.unsavedConsentStatus === true && e.isHidden === false;
        });
        const nonHiddenConsents = consents.filter((e) => {
            return e.isHidden === false;
        });
        if (givenConsents.length === 0) {
            return Consent.CategoryStatus.no;
        } else if (givenConsents.length === nonHiddenConsents.length) {
            return Consent.CategoryStatus.yes;
        } 
        return Consent.CategoryStatus.intermediate;
    }

    /**
     * Checks status of given consents to determine their overal status "yes", "no" or "intermediate"
     * @param consents 
     */
    static checkOverallStatusByCategory(categories: Consent.Category[]): Consent.CategoryStatus {
        const givenConsents = categories.filter((e) => {
            return e.status === true && e.isHidden === false;
        });
        const nonHiddenCategory = categories.filter((e) => {
            return e.isHidden === false;
        });
        if (givenConsents.length === 0) {
            return Consent.CategoryStatus.no;
        } else if (givenConsents.length === nonHiddenCategory.length) {
            return Consent.CategoryStatus.yes;
        } 
        return Consent.CategoryStatus.intermediate;
    }

    /**
     * same as consentService.changeCategorySelectedStatus
     * it'll set isIntermediateState = false
     * it'll change status of category and unsavedConsentStatus all consents within that category
     * @param updatedCategory 
     * @param isSelected 
     */
    static changeCategorySelectedStatus(updatedCategory: Consent.Category, isSelected: boolean): void {
        updatedCategory.consents.forEach(consent => {
            ConsentsStatusService.setUnsavedConsentStatus(consent, isSelected);
        });
    }

    /**
     * Change the status of one consent. Trigger change check of category and allState if nescessary
     * @param consent 
     * @param isSelected 
     */
    static setUnsavedConsentStatus(consent: Consent.Consent, isSelected: boolean): void {
        // Only change status if different
        if (consent.unsavedConsentStatus !== isSelected) {

            // Trigger check of category
            const parentCategory = GetConsentsService.getCategoryByConsent(consent);

            // can not set essential consent to false
            if (typeof parentCategory !== 'undefined' && parentCategory.isEssential && !isSelected) {
                return;
            }
            consent.unsavedConsentStatus = isSelected;

            // If this consent has no category, just trigger allStatus check
            if (typeof parentCategory === 'undefined' ) {
                ConsentsStatusService.setSelectAllState();
                return;
            }

            // If category is not in intermediate state, it means it must be in different state than consent
            if (parentCategory.isIntermediateState === false) {

                // If there are more than 1 consents, this means category must now be in intermediate state
                if (parentCategory.consents.length > 1) {
                    parentCategory.isIntermediateState = true;
                    parentCategory.status = false;
                } else {
                    // Otherwise this is the only consent of the category, so we can switch the category
                    parentCategory.status = isSelected;
                }

                // In either case this has triggered category status change, so check "allStatus"
                ConsentsStatusService.setSelectAllState();
            } else { 
                // Category was in intermediate state, we need to check all consents for changes
                ConsentsStatusService.setSelectedStateOfCategory(parentCategory);

                // If category is now not in intermediate state, status has changed so fire "allStatus" check
                if (parentCategory.isIntermediateState === false) {
                    ConsentsStatusService.setSelectAllState();
                }
            }
        }
    }

    /**
     * consentService.arrayOfConsentsThatHaveChanged
     * It'll filter out all consents whose consentStatus != unsavedConsentStatus
     * @param consents 
     */
    static getConsentsThatHaveChanged(consents: Consent.Consent[]): Consent.Consent[] {
        return consents.filter((e) => {
            return e.consentStatus !== e.unsavedConsentStatus || e.updatedBy === 'implicit';
        });
    }

    /**
     * consentService.arrayOfConsentsThatHaveChanged
     * It'll filter out all consents whose consentStatus != unsavedConsentStatus
     */
    static getCategoriesThatHaveChanged(categories: Consent.Category[]): Consent.Category[] {
        return categories.filter((category) => {
            return ConsentsStatusService.getConsentsThatHaveChanged(category.consents).length;
        });
    }

    /**
     * Marks all categories as essential to prevent changing the consents.
     * Is used for users outside of EU.
     * @param categories 
     */
    static setAllCategoriesToEssential(categories: Consent.Category[]): void {
        categories.forEach(category => {
            category.isEssential = true;
        });
    }

    /**
     * Sets the status for all the passed consents
     * @param consents 
     * @param status 
     */
    static updateAllConsentsStatus(consents: Consent.Consent[], status): void {
        consents.forEach(consent => {
            ConsentsStatusService.setUnsavedConsentStatus(consent, status);
        });
    }

    /**
     * It'll set unsavedConsentStatus and consentStatus to defaultConsentStatus
     */
    static resetConsentsToDefault(consents: Consent.Consent[]): void {
        consents.forEach(consent => {
            consent.consentStatus = consent.defaultConsentStatus;
            ConsentsStatusService.setUnsavedConsentStatus(consent, consent.defaultConsentStatus);
        });
    }

    /**
     * Resets all the consents to their original status.
     * Used if any interaction is aborted, for example closing the privacy settings side.
     * @param consents 
     */
    static resetAllConsents(consents: Consent.Consent[]): void {
        consents.forEach(consent => {
            ConsentsStatusService.setUnsavedConsentStatus(consent, consent.consentStatus);
        });
    }
}
